import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { EstudianteExamen } from '../models/estudiante-examen.model';
import { SharedService } from '../../../services/shared.service';
import { EstudianteService } from '../../../services/estudiante.service';
import { Graduacion } from '../../../models/graduacion.model';
import { Instructor } from '../../../models/instructor.model';
import { PreciosExamen, MaderaTipo } from '../models/precios-config.model';
import { ControlExamenService } from '../../../services/control-examen.service';
import { MaderaSeleccionada, PagoEstudiante } from '../models/estudiante-examen.model';
import { Observable, combineLatest } from 'rxjs';
import { take, map } from 'rxjs/operators';

@Component({
  selector: 'app-registro-recepcion',
  templateUrl: './registro-recepcion.component.html',
  styleUrls: ['./registro-recepcion.component.css']
})
export class RegistroRecepcionComponent implements OnInit {
  @Output() estudianteAgregado = new EventEmitter<EstudianteExamen>();

  registroForm: FormGroup;
  graduaciones$: Observable<Graduacion[]>;
  instructores$: Observable<Instructor[]>;

  preciosExamen$: Observable<PreciosExamen>;
  preciosExamen: PreciosExamen = new PreciosExamen();
  maderasSeleccionadas: MaderaSeleccionada[] = [];
  maderasSectionExpanded: boolean = false;
  incluyeExamen: boolean = true;
  incluyeLibreta: boolean = false;
  precioIndumentaria: number = 0;
  indumentariaDescripcion: string = '';
  
  // Discount properties
  descuentoPorcentaje: number = 0;
  totalPago: number = 0;
  descuentoValor: number = 0; // Value in $
  
  showIndividualDiscounts: boolean = false;
  
  // Individual discounts
  descExamenPct: number = 0;
  descLibretaPct: number = 0;
  descMaderasPct: number = 0;
  descIndumentariaPct: number = 0;
  
  // Values after individual discounts
  valorExamen: number = 0;
  valorLibreta: number = 0;
  valorMaderas: number = 0;
  valorIndumentaria: number = 0;

  medioDePago: string = 'Efectivo';
  
  isLoading = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  studentNotFound = false;

  constructor(
    private fb: FormBuilder,
    private sharedService: SharedService,
    private estudianteService: EstudianteService,
    private controlExamenService: ControlExamenService // Add this service
  ) {
    this.graduaciones$ = this.sharedService.graduaciones$.pipe(
      map(graduaciones => this.sortGraduaciones(graduaciones))
    );
    this.instructores$ = this.sharedService.instructores$.pipe(
      map(instructores => this.sortInstructores(instructores))
    );
    
    this.registroForm = this.fb.group({
      nroLibreta: ['', [Validators.required, Validators.min(1)]],
      apellido: ['', [Validators.required, Validators.minLength(2)]],
      nombre: ['', [Validators.required, Validators.minLength(2)]],
      idInstructor: ['', [Validators.required]],
      idGraduacionActual: ['', [Validators.required]],
      idGraduacionExamen: ['', [Validators.required]]
    });

      // Get precio examen configuration
      this.preciosExamen$ = this.controlExamenService.preciosExamen$;
  }

  ngOnInit(): void {
    // Watch for nroLibreta changes to auto-fill data
    this.registroForm.get('nroLibreta')?.valueChanges.subscribe(value => {
      if (value && value > 0) {
        this.buscarEstudiante(value);
      } else {
        // When libreta is empty or 0, clear all fields except libreta
        this.clearForm(false);
      }
    });
    // Subscribe to precios
    this.preciosExamen$.subscribe(precios => {
      this.preciosExamen = precios;
      this.calcularTotal();
    });
  }

  // Sort graduaciones: GUPs (10 to 1), then DANs (1 to 9)
  private sortGraduaciones(graduaciones: Graduacion[]): Graduacion[] {
    return graduaciones.sort((a, b) => {
      // Separate GUPs and DANs
      const aIsGup = a.gup !== null && a.gup !== undefined;
      const bIsGup = b.gup !== null && b.gup !== undefined;
      
      // If both are GUPs, sort by gup descending (10, 9, 8, ..., 1)
      if (aIsGup && bIsGup) {
        return (b.gup || 0) - (a.gup || 0);
      }
      
      // If both are DANs, sort by dan ascending (1, 2, 3, ..., 9)
      if (!aIsGup && !bIsGup) {
        return (a.dan || 0) - (b.dan || 0);
      }
      
      // GUPs come before DANs
      return aIsGup ? -1 : 1;
    });
  }

  // Sort instructors: active first, then inactive at the end
  private sortInstructores(instructores: Instructor[]): Instructor[] {
    return instructores.sort((a, b) => {
      // First, sort by active status (active first)
      if (a.activo !== b.activo) {
        return a.activo ? -1 : 1;
      }
      
      // For active instructors, sort by id ascending
      if (a.activo && b.activo) {
        return a.id - b.id;
      }
      
      // For inactive instructors, sort by apellido alphabetically
      return a.apellido.localeCompare(b.apellido);
    });
  }

  buscarEstudiante(nroLibreta: number): void {
    this.isLoading = true;
    this.errorMessage = null;
    this.studentNotFound = false;

    this.estudianteService.getEstudiante(nroLibreta).subscribe(
      (estudiante) => {
        this.isLoading = false;
        if (estudiante) {
          // Auto-fill form with student data AND clear graduation fields first
          this.registroForm.patchValue({
            apellido: estudiante.apellido,
            nombre: estudiante.nombre,
            idInstructor: estudiante.idInstructor || '',
            idGraduacionActual: '',
            idGraduacionExamen: ''  
          });
          this.studentNotFound = false;
          
          // Try to get current graduation (this might set values or leave them empty)
          this.obtenerGraduacionActual(nroLibreta);
        } else {
          this.studentNotFound = true;
          this.clearFormExceptLibreta();
        }
      },
      (error) => {
        this.isLoading = false;
        this.studentNotFound = true;
        this.clearFormExceptLibreta();
        console.error('Error searching student:', error);
      }
    );
  }

  obtenerGraduacionActual(nroLibreta: number): void {
    combineLatest([
      this.estudianteService.getGraduacion(nroLibreta),
      this.graduaciones$.pipe(take(1))
    ]).subscribe(
      ([graduacion, graduaciones]) => {
        if (graduacion && graduaciones.length > 0) {
          const maxGraduacionId = Math.max(...graduaciones.map(g => g.id));
          
          if (graduacion.id === maxGraduacionId) {
            // If the current graduation is the last one, set exam graduation to the same
            this.registroForm.patchValue({
              idGraduacionActual: graduacion.id,
              idGraduacionExamen: graduacion.id // Same graduation for exam
            });
          } else if (graduacion.id < maxGraduacionId) {
            // Otherwise, set exam graduation to the next one
            this.registroForm.patchValue({
              idGraduacionActual: graduacion.id,
              idGraduacionExamen: graduacion.id + 1 // Default to current graduation +1 for exam
            });
          }
        } else {
          // Student found but no exams recorded - leave selects empty for manual selection
          this.registroForm.patchValue({
            idGraduacionActual: '', // Empty - user must select
            idGraduacionExamen: ''   // Empty - user must select
          });
        }
      },
      (error) => {  
        console.error('Error fetching current graduation:', error);
        // On error, also leave empty for manual selection
        this.registroForm.patchValue({
          idGraduacionActual: '', // Empty - user must select
          idGraduacionExamen: ''  // Empty - user must select
        });
      }
    );
  }

  toggleMaderasSection(): void {
    this.maderasSectionExpanded = !this.maderasSectionExpanded;
  }

  agregarMadera(tipo: MaderaTipo): void {
    const index = this.maderasSeleccionadas.findIndex(m => m.tipo.id === tipo.id);
    if (index >= 0) {
      // Increment existing
      this.maderasSeleccionadas[index].cantidad++;
    } else {
      // Add new
      this.maderasSeleccionadas.push({ tipo, cantidad: 1 });
    }
    this.calcularTotal();
  }

  quitarMadera(tipo: MaderaTipo): void {
    const index = this.maderasSeleccionadas.findIndex(m => m.tipo.id === tipo.id);
    if (index >= 0) {
      if (this.maderasSeleccionadas[index].cantidad > 1) {
        // Decrement
        this.maderasSeleccionadas[index].cantidad--;
      } else {
        // Remove
        this.maderasSeleccionadas.splice(index, 1);
      }
      this.calcularTotal();
    }
  }

  calcularTotal(): void {
    const subtotalExamen = this.incluyeExamen ? this.preciosExamen.examen : 0;
    const subtotalLibreta = this.incluyeLibreta ? this.preciosExamen.libreta : 0;
    const subtotalMaderas = this.maderasSeleccionadas.reduce((acc, current) => acc + (current.tipo.precio * current.cantidad), 0);
    const subtotalIndumentaria = this.precioIndumentaria;

    if (!this.showIndividualDiscounts) {
      const subtotal = subtotalExamen + subtotalLibreta + subtotalMaderas + subtotalIndumentaria;
      this.descuentoValor = (subtotal * this.descuentoPorcentaje) / 100;
      this.totalPago = Math.round((subtotal - this.descuentoValor) * 100) / 100;
      
      // Sync individual values for display
      this.valorExamen = Math.round((subtotalExamen - (subtotalExamen * this.descuentoPorcentaje / 100)) * 100) / 100;
      this.valorLibreta = Math.round((subtotalLibreta - (subtotalLibreta * this.descuentoPorcentaje / 100)) * 100) / 100;
      this.valorMaderas = Math.round((subtotalMaderas - (subtotalMaderas * this.descuentoPorcentaje / 100)) * 100) / 100;
      this.valorIndumentaria = Math.round((subtotalIndumentaria - (subtotalIndumentaria * this.descuentoPorcentaje / 100)) * 100) / 100;
    } else {
      // Individual discounts
      this.valorExamen = Math.round((subtotalExamen - (subtotalExamen * this.descExamenPct / 100)) * 100) / 100;
      this.valorLibreta = Math.round((subtotalLibreta - (subtotalLibreta * this.descLibretaPct / 100)) * 100) / 100;
      this.valorMaderas = Math.round((subtotalMaderas - (subtotalMaderas * this.descMaderasPct / 100)) * 100) / 100;
      this.valorIndumentaria = Math.round((subtotalIndumentaria - (subtotalIndumentaria * this.descIndumentariaPct / 100)) * 100) / 100;
      
      this.totalPago = Math.round((this.valorExamen + this.valorLibreta + this.valorMaderas + this.valorIndumentaria) * 100) / 100;
      const subtotal = subtotalExamen + subtotalLibreta + subtotalMaderas + subtotalIndumentaria;
      this.descuentoValor = Math.round((subtotal - this.totalPago) * 100) / 100;
      // Note: we don't update total percentage here as it's mixed
    }
  }

  onTotalPagoChange(): void {
    const subtotalExamen = this.incluyeExamen ? this.preciosExamen.examen : 0;
    const subtotalLibreta = this.incluyeLibreta ? this.preciosExamen.libreta : 0;
    const subtotalMaderas = this.maderasSeleccionadas.reduce((acc, current) => acc + (current.tipo.precio * current.cantidad), 0);
    const subtotalIndumentaria = this.precioIndumentaria;
    const subtotal = subtotalExamen + subtotalLibreta + subtotalMaderas + subtotalIndumentaria;

    if (subtotal > 0) {
      this.descuentoPorcentaje = Math.round(((subtotal - this.totalPago) / subtotal) * 10000) / 100;
      this.descuentoValor = Math.round((subtotal - this.totalPago) * 100) / 100;
      
      // Distribute global discount to individual fields for display
      this.valorExamen = Math.round((subtotalExamen - (subtotalExamen * this.descuentoPorcentaje / 100)) * 100) / 100;
      this.valorLibreta = Math.round((subtotalLibreta - (subtotalLibreta * this.descuentoPorcentaje / 100)) * 100) / 100;
      this.valorMaderas = Math.round((subtotalMaderas - (subtotalMaderas * this.descuentoPorcentaje / 100)) * 100) / 100;
      this.valorIndumentaria = Math.round((subtotalIndumentaria - (subtotalIndumentaria * this.descuentoPorcentaje / 100)) * 100) / 100;
      
      // Update individual percentages as well
      this.descExamenPct = this.descuentoPorcentaje;
      this.descLibretaPct = this.descuentoPorcentaje;
      this.descMaderasPct = this.descuentoPorcentaje;
      this.descIndumentariaPct = this.descuentoPorcentaje;
    }
  }

  onIndividualValueChange(item: 'examen' | 'libreta' | 'maderas' | 'indumentaria'): void {
    let subtotalItem = 0;
    let valorItem = 0;

    switch(item) {
      case 'examen': 
        subtotalItem = this.incluyeExamen ? this.preciosExamen.examen : 0;
        valorItem = this.valorExamen;
        if (subtotalItem > 0) this.descExamenPct = Math.round(((subtotalItem - valorItem) / subtotalItem) * 10000) / 100;
        break;
      case 'libreta':
        subtotalItem = this.incluyeLibreta ? this.preciosExamen.libreta : 0;
        valorItem = this.valorLibreta;
        if (subtotalItem > 0) this.descLibretaPct = Math.round(((subtotalItem - valorItem) / subtotalItem) * 10000) / 100;
        break;
      case 'maderas':
        subtotalItem = this.maderasSeleccionadas.reduce((acc, current) => acc + (current.tipo.precio * current.cantidad), 0);
        valorItem = this.valorMaderas;
        if (subtotalItem > 0) this.descMaderasPct = Math.round(((subtotalItem - valorItem) / subtotalItem) * 10000) / 100;
        break;
      case 'indumentaria':
        subtotalItem = this.precioIndumentaria;
        valorItem = this.valorIndumentaria;
        if (subtotalItem > 0) this.descIndumentariaPct = Math.round(((subtotalItem - valorItem) / subtotalItem) * 10000) / 100;
        break;
    }
    
    // Recalculate totals based on the manual values
    this.totalPago = Math.round((this.valorExamen + this.valorLibreta + this.valorMaderas + this.valorIndumentaria) * 100) / 100;
    const subtotalExamenT = this.incluyeExamen ? this.preciosExamen.examen : 0;
    const subtotalLibretaT = this.incluyeLibreta ? this.preciosExamen.libreta : 0;
    const subtotalMaderasT = this.maderasSeleccionadas.reduce((acc, current) => acc + (current.tipo.precio * current.cantidad), 0);
    const subtotalIndumentariaT = this.precioIndumentaria;
    const subtotalTotal = subtotalExamenT + subtotalLibretaT + subtotalMaderasT + subtotalIndumentariaT;
    
    this.descuentoValor = Math.round((subtotalTotal - this.totalPago) * 100) / 100;
    if (subtotalTotal > 0) {
      this.descuentoPorcentaje = Math.round(((subtotalTotal - this.totalPago) / subtotalTotal) * 10000) / 100;
    }
  }

  toggleIndividualDiscounts(): void {
    this.showIndividualDiscounts = !this.showIndividualDiscounts;
    if (this.showIndividualDiscounts) {
      // Initialize individual percentages with the general one
      this.descExamenPct = this.descuentoPorcentaje;
      this.descLibretaPct = this.descuentoPorcentaje;
      this.descMaderasPct = this.descuentoPorcentaje;
      this.descIndumentariaPct = this.descuentoPorcentaje;
    }
    this.calcularTotal();
  }

  clearForm(includeLibreta: boolean = true): void {
    if (includeLibreta) {
      this.registroForm.reset();
      // After reset, ensure empty strings for selects
      this.registroForm.patchValue({
        idGraduacionActual: '',
        idGraduacionExamen: '',
        idInstructor: ''
      });
    } else {
      this.registroForm.patchValue({
        apellido: '',
        nombre: '',
        idInstructor: '',
        idGraduacionActual: '', 
        idGraduacionExamen: ''  
      });
    }
    // Clear payment info
    this.incluyeExamen = true;
    this.incluyeLibreta = false;
    this.maderasSeleccionadas = [];
    this.precioIndumentaria = 0;
    this.indumentariaDescripcion = '';
    this.descuentoPorcentaje = 0;
    this.descuentoValor = 0;
    this.showIndividualDiscounts = false;
    this.descExamenPct = 0;
    this.descLibretaPct = 0;
    this.descMaderasPct = 0;
    this.descIndumentariaPct = 0;
    this.valorExamen = 0;
    this.valorLibreta = 0;
    this.valorMaderas = 0;
    this.valorIndumentaria = 0;
    this.medioDePago = 'Efectivo';
    this.calcularTotal();

    this.errorMessage = null;
    this.successMessage = null;
    this.studentNotFound = false;
  }

  clearFormExceptLibreta(): void {
    this.registroForm.patchValue({
      apellido: '',
      nombre: '',
      idInstructor: '',
      idGraduacionActual: '',
      idGraduacionExamen: '' 
    });
  }

  onSubmit(): void {
    if (this.registroForm.valid) {
      const formData = this.registroForm.value;
      
      // Create payment data
      const pagoData: PagoEstudiante = {
        incluyeExamen: this.incluyeExamen,
        incluyeLibreta: this.incluyeLibreta,
        maderasSeleccionadas: [...this.maderasSeleccionadas],
        precioIndumentaria: this.precioIndumentaria,
        indumentariaDescripcion: this.indumentariaDescripcion,
        descuentoPorcentaje: this.descuentoPorcentaje,
        descuentoValor: this.descuentoValor,
        descExamenPct: this.descExamenPct,
        descLibretaPct: this.descLibretaPct,
        descMaderasPct: this.descMaderasPct,
        descIndumentariaPct: this.descIndumentariaPct,
        valorExamen: this.valorExamen,
        valorLibreta: this.valorLibreta,
        valorMaderas: this.valorMaderas,
        valorIndumentaria: this.valorIndumentaria,
        medioDePago: this.medioDePago,
        total: this.totalPago,
        pagado: true
      };
      
      const estudianteExamen = EstudianteExamen.fromReceptionData({
        nroLibreta: parseInt(formData.nroLibreta),
        apellido: formData.apellido.trim().toUpperCase(),
        nombre: formData.nombre.trim().toUpperCase(),
        idInstructor: parseInt(formData.idInstructor),
        idGraduacionActual: parseInt(formData.idGraduacionActual),
        idGraduacionExamen: parseInt(formData.idGraduacionExamen),
        pago: pagoData // Add payment data
      });

      this.estudianteAgregado.emit(estudianteExamen);
      
      this.successMessage = 'Estudiante registrado correctamente';
      this.errorMessage = null;
      
      // Clear form after successful submission
      setTimeout(() => {
        this.clearForm();
        this.successMessage = null;
      }, 2000);
    } else {
      this.errorMessage = 'Por favor complete todos los campos requeridos';
      this.markFormGroupTouched();
    }
  }

  // Method to load existing student data into the form (for editing)
  public cargarDatosEstudiante(estudiante: EstudianteExamen): void {
    this.registroForm.patchValue({
      nroLibreta: estudiante.nroLibreta,
      apellido: estudiante.apellido,
      nombre: estudiante.nombre,
      idInstructor: estudiante.idInstructor,
      idGraduacionActual: estudiante.idGraduacionActual,
      idGraduacionExamen: estudiante.idGraduacionExamen
    });
    
    if (estudiante.pago) {
      this.incluyeExamen = estudiante.pago.incluyeExamen;
      this.incluyeLibreta = estudiante.pago.incluyeLibreta;
      this.precioIndumentaria = estudiante.pago.precioIndumentaria;
      this.indumentariaDescripcion = estudiante.pago.indumentariaDescripcion || '';
      this.descuentoPorcentaje = estudiante.pago.descuentoPorcentaje;
      this.descuentoValor = estudiante.pago.descuentoValor ?? 0;
      this.descExamenPct = estudiante.pago.descExamenPct ?? 0;
      this.descLibretaPct = estudiante.pago.descLibretaPct ?? 0;
      this.descMaderasPct = estudiante.pago.descMaderasPct ?? 0;
      this.descIndumentariaPct = estudiante.pago.descIndumentariaPct ?? 0;
      this.valorExamen = estudiante.pago.valorExamen ?? 0;
      this.valorLibreta = estudiante.pago.valorLibreta ?? 0;
      this.valorMaderas = estudiante.pago.valorMaderas ?? 0;
      this.valorIndumentaria = estudiante.pago.valorIndumentaria ?? 0;
      this.medioDePago = estudiante.pago.medioDePago;
      this.maderasSeleccionadas = estudiante.pago.maderasSeleccionadas ? [...estudiante.pago.maderasSeleccionadas] : [];
      this.totalPago = estudiante.pago.total;
      this.showIndividualDiscounts = (this.descExamenPct !== this.descLibretaPct ||
        this.descExamenPct !== this.descMaderasPct ||
        this.descExamenPct !== this.descIndumentariaPct) &&
        (this.descExamenPct > 0 || this.descLibretaPct > 0 || this.descMaderasPct > 0 || this.descIndumentariaPct > 0);
    }
    
    this.successMessage = `Editando estudiante: ${estudiante.nombre} ${estudiante.apellido} (Libreta ${estudiante.nroLibreta})`;
    setTimeout(() => {
      this.successMessage = null;
    }, 3000);
  }

  public limpiarFormulario(): void {
    this.registroForm.reset({
      nroLibreta: null,
      apellido: '',
      nombre: '',
      idInstructor: null,
      idGraduacionActual: null,
      idGraduacionExamen: null
    });
    
    // Reset payment info
    this.incluyeExamen = true;
    this.incluyeLibreta = false;
    this.precioIndumentaria = 0;
    this.indumentariaDescripcion = '';
    this.descuentoPorcentaje = 0;
    this.medioDePago = 'Efectivo';
    this.maderasSeleccionadas = [];
    this.maderasSectionExpanded = false;
    this.totalPago = 0;
    
    // Clear messages
    this.errorMessage = null;
    this.successMessage = null;
    this.studentNotFound = false;
  }

  private markFormGroupTouched(): void {
    Object.keys(this.registroForm.controls).forEach(key => {
      const control = this.registroForm.get(key);
      control?.markAsTouched();
    });
  }

  getFieldError(fieldName: string): string {
    const control = this.registroForm.get(fieldName);
    if (control?.errors && control.touched) {
      if (control.errors['required']) return `${this.getFieldNameDescription(fieldName)} es un campo requerido`;
      if (control.errors['min']) return `${fieldName} debe ser mayor a 0`;
      if (control.errors['minlength']) return `${fieldName} debe tener al menos 2 caracteres`;
    }
    return '';
  }

  private getFieldNameDescription(fieldName: string): string {
    switch (fieldName) {
      case 'nroLibreta':
        return 'Número de libreta';
      case 'apellido':
        return 'Apellido';
      case 'nombre':
        return 'Nombre';
      case 'idInstructor':
        return 'Instructor';
      case 'idGraduacionActual':
        return 'Graduación actual';
      case 'idGraduacionExamen':
        return 'Graduación examen';
      default:
        return fieldName;
    }
  }

}